/**
 * Page de paramètres de l'application
 * 
 * Cette page permet de gérer les paramètres de l'application et les données de test en mode développement.
 * 
 * Types d'activités disponibles :
 * 1. Restaurant (restaurant)
 * 2. Boulangerie (boulangerie)
 * 3. Salon de Coiffure (coiffure)
 * 4. Auto-École (autoecole)
 * 5. Magasin de Vêtements (vetements)
 * 6. Service de Réparation (reparation)
 * 7. Accessoires Électroniques (electronique)
 * 8. Épicerie (epicerie)
 * 
 * Chaque activité contient les données suivantes :
 * - Catégories
 * - Produits
 * - Clients
 * - Paramètres
 * - Ventes
 * - Articles de vente
 * - Paiements
 * - Mouvements de stock
 * - Modèles de vente
 * - Valeurs de champs personnalisés
 * 
 * Fonctionnalités :
 * 1. Mode Développement (visible uniquement en NODE_ENV === 'development')
 *    - Gestion des données de test via TestDataManager
 *    - Sélection et chargement d'une activité
 *    - Réinitialisation des données d'une activité
 *    - Réinitialisation complète de la base de données de sauvegarde
 *    - Affichage des messages de statut et de la dernière mise à jour
 * 
 * 2. Configuration générale (à implémenter)
 *    - Section réservée pour les paramètres généraux de l'application
 * 
 * Dépendances :
 * - TestDataManager : Service de gestion des données de test
 * - useLanguage : Hook pour la gestion des langues
 * - useMyAppStore : Store global de l'application
 * 
 * @component
 * @returns {JSX.Element} Composant Settings
 */

import React, { useEffect, useState } from 'react';
import { TestDataManager } from '../../../../services/AppDataServices/TestDataManager';
import { useLanguage } from '../../../../hooks/useLanguage';
import { aside as frAside } from '../../../../locales/fr/aside';
import { aside as arAside } from '../../../../locales/ar/aside';
import { aside as esAside } from '../../../../locales/es/aside';
import useMyAppStore from '../../../../stores/myAppStore';

const translations = {
  fr: frAside,
  ar: arAside,
  es: esAside
};

const Settings = () => {
  const { lang } = useLanguage();
  const t = translations[lang];
  const { 
    setPageTitle, 
    setCurrentActivity,
    currentActivity: storeCurrentActivity 
  } = useMyAppStore();
  const isDevelopment = true; // Toujours activer en production pour les tests
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    setPageTitle(t.routes.settings);
  }, [lang, t.routes.settings, setPageTitle]);

  useEffect(() => {
    const initializeData = async () => {
      try {
        const acts = await TestDataManager.getAvailableActivities();
        setActivities(Array.isArray(acts) ? acts : []);
      } catch (error) {
        setActivities([]);
        console.error('Erreur lors de l\'initialisation:', error);
      }
    };
    initializeData();
  }, []);

  const handleActivityChange = async (activityId) => {
    if (!activityId) return;
    
    setIsLoading(true);
    setMessage('');
    try {
      await TestDataManager.loadActivityData(activityId);
      const activityInfo = await TestDataManager.getActivityInfo(activityId);

      setCurrentActivity({
        id: activityId,
        name: activityInfo.name,
        lastUpdated: new Date(activityInfo.lastUpdated)
      });

      setMessage('Données chargées avec succès !');
    } catch (error) {
      console.error('❌ Erreur lors du changement d\'activité:', error);
      setMessage('Erreur lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetActivity = async () => {
    if (!storeCurrentActivity) return;

    // Demander confirmation
    const confirmMessage = `Êtes-vous sûr de vouloir réinitialiser les données de l'activité "${storeCurrentActivity.name}" ?\nToutes les modifications seront perdues.`;
    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    setMessage('');
    try {
      await TestDataManager.resetActivityData(storeCurrentActivity.id);
      setMessage('Données réinitialisées avec succès !');
    } catch (error) {
      setMessage('Erreur lors de la réinitialisation des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetBackupDatabase = async () => {
    // Demander confirmation
    const confirmMessage = 'Êtes-vous sûr de vouloir réinitialiser TOUTES les données ?\nCette action va :\n- Supprimer toutes les données de toutes les activités\n- Restaurer les données initiales\n- Cette action est irréversible';
    if (!window.confirm(confirmMessage)) return;

    setIsLoading(true);
    setMessage('');
    try {
      // Réinitialiser la base de données de sauvegarde
      await TestDataManager.resetBackupDatabase();
      
      // Réinitialiser les données de l'activité courante si elle existe
      if (storeCurrentActivity) {
        await TestDataManager.resetActivityData(storeCurrentActivity.id);
      }
      
      // Recharger la liste des activités
      setActivities(TestDataManager.getAvailableActivities());
      
      // Réinitialiser l'activité courante dans le store
      setCurrentActivity(null);
      
      setMessage('Base de données de sauvegarde et données d\'activité réinitialisées avec succès !');
    } catch (error) {
      console.error('Erreur lors de la réinitialisation:', error);
      setMessage('Erreur lors de la réinitialisation de la base de données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCleanDuplicateSettings = async () => {
    setIsLoading(true);
    setMessage('');
    try {
      const deletedCount = await TestDataManager.cleanDuplicateSettings();
      setMessage(`${deletedCount} doublons supprimés avec succès !`);
    } catch (error) {
      console.error('Erreur lors du nettoyage:', error);
      setMessage('Erreur lors du nettoyage des doublons');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container mx-auto">
      <h1 className="text-2xl font-bold mb-4">Paramètres</h1>
      <div className="bg-white rounded-lg shadow p-6">
        {isDevelopment && (
          <div className="mb-8 p-4 border border-yellow-200 bg-yellow-50 rounded-lg">
            <h2 className="text-lg font-semibold text-yellow-800 mb-4">Mode Développement</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gestion des données de test
                </label>
                <div className="flex gap-4 items-end">
                  <div className="flex-1">
                    <select
                      onChange={(e) => handleActivityChange(e.target.value)}
                      disabled={isLoading}
                      className="w-full border rounded-md p-2"
                      value={storeCurrentActivity?.id || ''}
                    >
                      <option value="">Choisir une activité</option>
                      {Array.isArray(activities) && activities.map(activity => (
                        <option key={activity.id} value={activity.id}>
                          {activity.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {storeCurrentActivity && (
                    <button
                      onClick={handleResetActivity}
                      disabled={isLoading}
                      className="px-4 py-2 bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                    >
                      Réinitialiser
                    </button>
                  )}
                </div>
                <div className="mt-4">
                  <button
                    onClick={handleResetBackupDatabase}
                    disabled={isLoading}
                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50"
                  >
                    Réinitialiser la base de sauvegarde
                  </button>
                  <button
                    onClick={handleCleanDuplicateSettings}
                    disabled={isLoading}
                    className="ml-2 px-4 py-2 bg-orange-100 text-orange-700 rounded-md hover:bg-orange-200 disabled:opacity-50"
                  >
                    Nettoyer les doublons
                  </button>
                </div>
                {storeCurrentActivity && (
                  <p className="mt-2 text-sm text-gray-600">
                    Dernière mise à jour : {storeCurrentActivity.lastUpdated.toLocaleString()}
                  </p>
                )}
                {message && (
                  <p className={`mt-2 text-sm ${message.includes('succès') ? 'text-green-600' : 'text-red-600'}`}>
                    {message}
                  </p>
                )}
              </div>
              <div className="mt-4 p-3 bg-blue-50 rounded-md">
                <h3 className="text-sm font-medium text-blue-800 mb-2">Comment ça marche ?</h3>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• Sélectionnez une activité pour charger ses données</li>
                  <li>• Les modifications sont automatiquement sauvegardées</li>
                  <li>• Utilisez "Réinitialiser" pour revenir aux données initiales</li>
                  <li>• Les données sont conservées entre les sessions</li>
                </ul>
              </div>
              <p className="text-sm text-yellow-700">
                Cette section n'est visible qu'en mode développement. Les données de test seront supprimées en production.
              </p>
            </div>
          </div>
        )}
        
        <div className="space-y-6">
          <h2 className="text-lg font-semibold">Configuration générale</h2>
          <p>Configuration des paramètres à venir...</p>
        </div>
      </div>
    </div>
  );
};

export default Settings; 